/**
 * sections.single-motion-image
 *
 * The template ships the <video> with `preload="none"` and no `autoplay`, so the
 * file is not requested until this module asks for it. That is the whole point:
 * phones never spend bandwidth on a desktop-only video, and the request only
 * starts once the section is near the viewport (app.js gates init on
 * isInViewport).
 *
 * Below 768px the video is display:none and we never touch it — the still image
 * is handled by <picture> in markup, no JS involved.
 *
 * Exactly ONE thing may kick off the load. Assigning `preload` runs the resource
 * selection algorithm on its own, and `load()` afterwards aborts that request and
 * starts a fresh one, so doing both fetches the file twice. `play()` loads by
 * itself, which covers the normal path.
 */

const DESKTOP = '(min-width: 768px)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

const isDataSaving = () => {
  const c = navigator.connection;

  if (!c) return false;

  return Boolean(c.saveData) || /^(slow-2g|2g|3g)$/.test(c.effectiveType || '');
};

export const init = section => {
  const video = section.querySelector('[data-motion]');

  if (!video) return;

  const desktop = window.matchMedia(DESKTOP);
  let requested = false;
  let visibility;

  const resume = () => {
    if (window.matchMedia(REDUCED_MOTION).matches) return;

    video.play().catch(() => {});
  };

  const request = () => {
    if (requested || !desktop.matches) return;

    // Respect an explicit data-saver or a slow connection: the frame's background
    // colour is a better outcome than a multi-megabyte download.
    if (isDataSaving()) return;

    requested = true;

    if (window.matchMedia(REDUCED_MOTION).matches) {
      // Buffer it so the first frame paints, but never start the motion.
      video.preload = 'auto';
    } else {
      resume();
    }

    // Stop decoding while the section is off screen — a looping video nobody can
    // see still costs CPU and battery.
    visibility = new IntersectionObserver(
      entries => entries.forEach(entry => (entry.isIntersecting ? resume() : video.pause())),
      { rootMargin: '200px 0px' },
    );
    visibility.observe(section);
  };

  request();
  desktop.addEventListener('change', request);

  return () => {
    desktop.removeEventListener('change', request);
    if (visibility) visibility.disconnect();
  };
};
